
import store from '../store';
import { RootState } from '../store/rootReducer';
import { ContextState } from '../store/context/reducer';
import { CandidateLevelWorkflowState } from '../store/common/constants';
import { AssessmentState } from '../store/assessment/reducer';


export type GradingPartyDocument = {
    marketplaceId: number;
    permissions: [];
    userAssessmentId: number;
    assessmentRunId: number;
    assessmentRunTitle?: string;
    attendance?: string;
    partyId: number;
    partyName: string;
    submissionStatus: string;
    evaluatorGrades: EvaluatorGrade[];
    evaluationRounds: EvaluationRound[];
    phaseEvaluationGrades?: EvaluatorGrade[];
    collaborativeMarking: boolean;
    includeOtherEvaluators: boolean;
    committeeId?: number;
    committeeName?: string;
    gradingCompleteOnCandidate: boolean;
    explanationOfGradesAdministrationEnabled: boolean;
    explanationOfGradesRequestStatus?: string;
    explanationOfGradeStatus?: string;
    externalDeliveryId?: number;
    externalDeliveryFileName?: string;
    externalDeliveryMimetype?: string;
    deliveryMode?: string;
    finalGrade?: string;
    finalGradeConfirmationDate?: string;
    /* used as helper when displaying round/committee combo */
    displayedRoundCommitteeId?: number;
    deliveryDeadline?: string;
    assessmentRunChildren?: [];
    isChild?: boolean;
    notDeliveredChild?: boolean;
    antiplagiarismReportShareStatus?: string;
    candidateSubmissionAccessLevel?: string;
}

export type EvaluatorGrade = {
    evaluatorGrade?: string;
    evaluatorId: number;
    evaluatorName: string;
    flagged?: boolean;
    gradeValueFromEvaluator?: number;
    gradeValueFromThreshold?: number;
    lastEdited?: string;
    allQuestionsMarked?: boolean;
    manuallySetGrade?: boolean;
    totalScore?: number;
    evaluationRound?: number;
    committeeId?: number;
    adminGradeApprovalStatus?: string;
    candidateLevelWorkflowState?: CandidateLevelWorkflowState;
    nextAttemptAllowed?: boolean;
}

export type EvaluationRound = {
    evaluationRound: number;
    committeeId?: number;
    grade?: string;
    adminGradeApprovalStatus?: string;
    jaggedAnalysisStatus?: string;
}

export const getEvaluationRoundByCommitteeId = (gradingPartyDocument: GradingPartyDocument, committeeId: number): EvaluationRound => {
    if (!gradingPartyDocument) {
        return null;
    }
    return gradingPartyDocument.evaluationRounds.find((evaluationRound) => {
        return evaluationRound.committeeId === committeeId;
    });
};

export const getEvaluationRound = (gradingPartyDocument: GradingPartyDocument, requestedEvaluationRound: number): EvaluationRound => {
    if (!gradingPartyDocument) {
        return null;
    }
    return gradingPartyDocument.evaluationRounds.find((evaluationRound) => {
        return evaluationRound.evaluationRound === requestedEvaluationRound;
    });
};

export const getLatestEvaluationRound = (gradingPartyDocument: GradingPartyDocument): EvaluationRound => {
    if (!gradingPartyDocument) {
        return null;
    }
    const latestRound = Math.max(...gradingPartyDocument.evaluationRounds.map((evaluationRound) => {
        return evaluationRound.evaluationRound;
    }));
    return gradingPartyDocument.evaluationRounds.find((evalRound) => evalRound.evaluationRound === latestRound);
};

export const getCurrentContextRound = (gradingPartyDocument: GradingPartyDocument): number => {
    const selectedCommitteeId = getPartyCommitteeIdForContext(gradingPartyDocument);
    // not loaded yet
    if (!gradingPartyDocument) {
        return null;
    }

    if (selectedCommitteeId > 0) {
        const round = getEvaluationRoundByCommitteeId(gradingPartyDocument, selectedCommitteeId);
        if (!round) {
            // stale data in state
            return null;
        }
        return round.evaluationRound;
    }
    const latestEvaluationRound = getLatestEvaluationRound(gradingPartyDocument);
    return latestEvaluationRound ? latestEvaluationRound.evaluationRound : 1;
};

export const getGradeForEvaluatorAndRound = (gradingPartyDocument: GradingPartyDocument,
    evaluatorId: number,
    collaborativeEvaluatorId: number,
    evaluationRound: number): EvaluatorGrade => {
    if (!gradingPartyDocument) {
        return null;
    }
    return gradingPartyDocument.evaluatorGrades.find((evalGrade: EvaluatorGrade) => {
        return evaluationRound === evalGrade.evaluationRound && (evalGrade.evaluatorId === evaluatorId
            || evalGrade.evaluatorId === collaborativeEvaluatorId);
    });
};

export const getGradesForRound = (gradingPartyDocument: GradingPartyDocument, evaluationRound: number) => {
    if (!gradingPartyDocument) {
        return [];
    }
    return gradingPartyDocument.evaluatorGrades.filter((evalGrade: EvaluatorGrade) => {
        return evalGrade.evaluationRound === evaluationRound;
    });
};

export const getCurrentCandidateLevelWorkflowState = (gradingPartyDocument: GradingPartyDocument,
    currentUserId: number): CandidateLevelWorkflowState => {
    const assessment: AssessmentState = (store.getState() as RootState).assessment;
    const { collaborativeEvaluatorId } = assessment;
    const currentRound = getCurrentContextRound(gradingPartyDocument);
    const evaluatorGrade = getGradeForEvaluatorAndRound(gradingPartyDocument, currentUserId, collaborativeEvaluatorId, currentRound);
    if (!evaluatorGrade) {
        return null;
    }
    return evaluatorGrade.candidateLevelWorkflowState;
};

/*
    If a party has been moved to another round, but it is being viewed in the context of a previous round,
    we need to make sure the proper committeeId is used
 */
export const getPartyCommitteeIdForContext = (gradingPartyDocument: GradingPartyDocument): number => {
    const contextState: ContextState = (store.getState() as RootState).context;
    const { selectedCommitteeId } = contextState;
    if (!gradingPartyDocument) {
        return selectedCommitteeId;
    }

    if (selectedCommitteeId > 0 && gradingPartyDocument.committeeId > 0 && selectedCommitteeId !== gradingPartyDocument.committeeId) {
        if (gradingPartyDocument) {
            // check for previous rounds
            const validPreviousRound = gradingPartyDocument
                .evaluationRounds
                .map((round) => round.committeeId)
                .includes(selectedCommitteeId);

            if (validPreviousRound) {
                return selectedCommitteeId;
            }
        } else {
            // grading party document not loaded yet, return -1 until it's loaded to avoid errors
            return -1;
        }
    }

    // return current party committee id if there is no committee context
    return gradingPartyDocument.committeeId;
};

export const getCommitteeIdForRound = (gradingPartyDocument: GradingPartyDocument, requestedEvaluationRound: number): number => {
    if (!gradingPartyDocument) {
        return null;
    }
    const evaluationRound: EvaluationRound = gradingPartyDocument
        .evaluationRounds
        .find((evalRound: EvaluationRound) => evalRound.evaluationRound === requestedEvaluationRound);
    if (evaluationRound) {
        return evaluationRound.committeeId;
    }
    return null;
};

export const getCurrentCandidateMarkingStatus = (gradingPartyDocument: GradingPartyDocument,
    currentUserId: number): boolean => {
    const assessment: AssessmentState = (store.getState() as RootState).assessment;
    const { collaborativeEvaluatorId } = assessment;
    const currentRound = getCurrentContextRound(gradingPartyDocument);
    const evaluatorGrade = getGradeForEvaluatorAndRound(gradingPartyDocument, currentUserId, collaborativeEvaluatorId, currentRound);
    if (!evaluatorGrade) {
        return null;
    }
    return evaluatorGrade.allQuestionsMarked;
};
